/**
 * Tracks the OPC schedule sync a user has kicked off, from the moment the
 * invoke-sync POST is accepted until the import_log row reaches a terminal
 * status.
 *
 * It lives outside React because the producer and the consumer have no common
 * owner: the sync button sits deep under pages/schedule/…, while the component
 * that announces completion is mounted by App.tsx above the project router (so
 * the notice still arrives after the user has navigated away). Putting the flag
 * on ProjectContext instead would re-render every grid in the project on each
 * transition.
 *
 * Leaf module — no imports — so it can be shared by the page, the alert and
 * auth-utils without creating import cycles.
 *
 * Mirrored to sessionStorage so a mid-job page reload still gets the notice.
 * sessionStorage rather than localStorage: per-tab, so two tabs cannot both
 * announce the same sync.
 */

export type TOpcSyncHandle = {
  /** import_log_id returned by the invoke-sync POST — what the poll reads. */
  importLogId: string;
  /** Guards against announcing one project's sync while viewing another. */
  projectId: string;
  /** Epoch ms. Drives the cap from wall-clock, so it survives a reload. */
  startedAt: number;
};

export const OPC_SYNC_STORAGE_KEY = "ciq:opc-sync";

/**
 * Longest a sync is tracked. Past this the handle is dropped silently — a job
 * that has not reported back in fifteen minutes is not one we can still say
 * anything useful about, and polling for it forever is worse than saying
 * nothing.
 */
export const OPC_SYNC_TIMEOUT_MS = 15 * 60 * 1000;

export const isOpcSyncExpired = (handle: TOpcSyncHandle): boolean =>
  Date.now() - handle.startedAt >= OPC_SYNC_TIMEOUT_MS;

type OpcSyncListener = (handle: TOpcSyncHandle | null) => void;

let current: TOpcSyncHandle | null = null;
let hydrated = false;
const listeners = new Set<OpcSyncListener>();

const readFromSession = (): TOpcSyncHandle | null => {
  try {
    const raw = sessionStorage.getItem(OPC_SYNC_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Anything that is not a complete handle is treated as absent rather than
    // trusted — this value survives reloads and deploys.
    if (
      !parsed?.importLogId ||
      !parsed?.projectId ||
      typeof parsed.startedAt !== "number"
    ) {
      return null;
    }
    return isOpcSyncExpired(parsed) ? null : (parsed as TOpcSyncHandle);
  } catch {
    // Unparseable, or sessionStorage unavailable (private mode).
    return null;
  }
};

const writeToSession = (handle: TOpcSyncHandle | null) => {
  try {
    if (handle) {
      sessionStorage.setItem(OPC_SYNC_STORAGE_KEY, JSON.stringify(handle));
    } else {
      sessionStorage.removeItem(OPC_SYNC_STORAGE_KEY);
    }
  } catch {
    // Storage is a convenience here; the in-memory handle still works without it.
  }
};

const emit = () => {
  listeners.forEach((cb) => {
    try {
      cb(current);
    } catch {
      // a failing listener must not stop the others from being told
    }
  });
};

export function getOpcSync(): TOpcSyncHandle | null {
  if (!hydrated) {
    hydrated = true;
    current = readFromSession();
    // A stored handle that was rejected (stale or malformed) is not resumable,
    // so drop the stored copy rather than re-reading it on the next hydrate.
    if (!current) writeToSession(null);
  }
  return current;
}

export function markOpcSyncStarted(handle: TOpcSyncHandle): void {
  hydrated = true;
  current = handle;
  writeToSession(handle);
  emit();
}

export function clearOpcSync(): void {
  const wasTracking = getOpcSync() !== null;
  current = null;
  writeToSession(null);
  // Idempotent: clearing when nothing is tracked must not wake listeners, or a
  // StrictMode double-effect would announce the same sync twice.
  if (wasTracking) emit();
}

export function onOpcSyncChange(cb: OpcSyncListener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
